import React, { FC, useState } from "react";
import { Link } from "react-router-dom";
import { currencySymbol } from "shared/CurrencySymbol";
import { RootState } from "redux/store";
import { useSelector } from "react-redux";
import { Property } from "redux/reducers/UserSlice/UserSliceType";
import BtnLikeIcon from "components/BtnLikeIcon/BtnLikeIcon";
import ShareButton from "components/ShareButton/ShareButton";

export interface StayCardProps {
  className?: string;
  data?: any;
  size?: "default" | "small";
  stayListings?: Property[];
  /** "propertyDetail" | "propertySearch" | undefined — tells BtnLikeIcon which slice to update */
  path?: string;
  favorites?: any;
  setFavorites?: React.Dispatch<React.SetStateAction<any[]>>;
}

const StayCard: FC<StayCardProps> = ({
  size = "default",
  className = "",
  data,
  stayListings,
  path,
  favorites,
  setFavorites,
}) => {
  const {
    place_images,
    title,
    price_per_month,
    id,
    slug,
    city,
    state,
    neighborhood,
    total_bedrooms,
    total_bathrooms,
    total_rooms,
    guest_number,
    instant_booking,
    amenities,
    average,
    is_favorite,
    property_type,
    property_category,
    cover_image,
  } = data || {};

  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const { currentUser } = useSelector((state: RootState) => state.user);
  const href = `/listing-stay-detail/${slug}`;

  // Build image array — prefer place_images, fall back to cover_image
  const images: string[] = Array.isArray(place_images) && place_images.length > 0
    ? place_images
    : cover_image
    ? [cover_image]
    : [];

  const hasMultipleImages = images.length > 1;

  const handlePrev = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setCurrentImageIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1));
  };

  const handleNext = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setCurrentImageIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1));
  };

  const rating = average?.average_review;
  const reviewCount = average?.count || 0;

  const propertyTypeName = property_type?.name || property_type;
  const propertyCategoryName = property_category?.name || property_category;

  // Top amenities to show as pills (max 3)
  const topAmenities: { id: number; name: string }[] = Array.isArray(amenities)
    ? amenities.slice(0, 3)
    : [];

  return (
    <div
      className={`nc-StayCard group relative bg-white dark:bg-neutral-900 rounded-2xl overflow-hidden border border-gray-100 dark:border-neutral-800 hover:shadow-xl hover:border-[#f5694b]/30 transition-all duration-300 ${className}`}
      data-nc-id="StayCard"
    >
      {/* ── IMAGE GALLERY ───────────────────────────────── */}
      <Link to={href} className="block relative aspect-[4/3] overflow-hidden bg-gray-100 dark:bg-neutral-800">
        {images[currentImageIndex] ? (
          <img
            src={images[currentImageIndex]}
            alt={`${title} - image ${currentImageIndex + 1}`}
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <svg className="w-12 h-12 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
          </div>
        )}

        {/* Overlay gradient */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent pointer-events-none" />

        {/* Nav arrows */}
        {hasMultipleImages && (
          <>
            <button onClick={handlePrev}
              className="absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 bg-white/90 hover:bg-white rounded-full shadow flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity z-10">
              <svg className="w-4 h-4 text-gray-800" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <button onClick={handleNext}
              className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 bg-white/90 hover:bg-white rounded-full shadow flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity z-10">
              <svg className="w-4 h-4 text-gray-800" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" />
              </svg>
            </button>

            {/* Dot indicators */}
            <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1 z-10">
              {images.slice(0, 5).map((_: any, i: number) => (
                <div key={i} className={`w-1.5 h-1.5 rounded-full transition-colors ${i === currentImageIndex ? "bg-white" : "bg-white/50"}`} />
              ))}
            </div>
          </>
        )}

        {/* Top badges */}
        <div className="absolute top-3 left-3 flex flex-col gap-1.5 z-10">
          {instant_booking && (
            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-bold bg-[#f5694b] text-white shadow">
              ⚡ Instant
            </span>
          )}
          {data?.featured && (
            <span className="inline-flex items-center px-2 py-1 rounded-full text-[10px] font-bold bg-amber-400 text-amber-900 shadow">
              ★ Featured
            </span>
          )}
        </div>

        {/* Like button — the wrapper stops the click from reaching the <Link>.
            propertyId / stayListings / path are what BtnLikeIcon needs to work. */}
        <div
  className="absolute top-3 right-3 z-20 flex items-center gap-2"
  onClick={(e) => {
    e.preventDefault();
    e.stopPropagation();
  }}
>
  <ShareButton
    propertyId={id}
    slug={slug}
    title={title}
    city={city}
    surface="stay_card"
  />
         {currentUser?.is_student && (
    <BtnLikeIcon
      propertyId={id}
      isLiked={is_favorite}
      stayListings={stayListings}
      path={path}
      favorites={favorites}
      setFavorites={setFavorites}
      className="w-8 h-8"
      colorClass="bg-white/90 hover:bg-white text-neutral-700"
    />
  )}
        </div>

        {/* Image counter */}
        {hasMultipleImages && (
          <div className="absolute bottom-3 right-3 bg-black/60 text-white px-1.5 py-0.5 rounded text-[10px] font-medium backdrop-blur-sm z-10">
            {currentImageIndex + 1}/{images.length}
          </div>
        )}
      </Link>

      {/* ── CONTENT ─────────────────────────────────────── */}
      <div className="p-4">

        {/* Type + Category badges */}
        <div className="flex items-center gap-1.5 flex-wrap mb-2">
          {propertyTypeName && (
            <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-[#f5694b]/10 text-[#f5694b] border border-[#f5694b]/20">
              {propertyTypeName}
            </span>
          )}
          {propertyCategoryName && (
            <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-gray-100 dark:bg-neutral-700 text-gray-600 dark:text-gray-300">
              {propertyCategoryName}
            </span>
          )}
        </div>

        {/* Title */}
        <Link to={href}>
          <h3 className="text-sm font-bold text-gray-900 dark:text-white line-clamp-1 mb-1.5 hover:text-[#f5694b] transition-colors leading-snug">
            {title}
          </h3>
        </Link>

        {/* Location */}
        <div className="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400 mb-3">
          <svg className="w-3 h-3 flex-shrink-0 text-[#f5694b]" fill="currentColor" viewBox="0 0 24 24">
            <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" />
          </svg>
          <span className="line-clamp-1">
            {neighborhood ? `${neighborhood}, ${city}` : city}
            {state ? `, ${state}` : ""}
          </span>
        </div>

        {/* Room details */}
        {(total_bedrooms || total_bathrooms || total_rooms || guest_number) && (
          <div className="flex items-center gap-3 text-xs text-gray-500 dark:text-gray-400 mb-3 pb-3 border-b border-gray-100 dark:border-neutral-700">
            {total_bedrooms != null && (
              <div className="flex items-center gap-1">
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12h18M3 6h18M3 18h18" />
                </svg>
                <span>{total_bedrooms} bed{total_bedrooms !== 1 ? "s" : ""}</span>
              </div>
            )}
            {total_bathrooms != null && (
              <div className="flex items-center gap-1">
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 14v3m4-3v3m4-3v3M3 21h18M3 10h18V7a4 4 0 00-4-4H7a4 4 0 00-4 4v3z" />
                </svg>
                <span>{total_bathrooms} bath{total_bathrooms !== 1 ? "s" : ""}</span>
              </div>
            )}
            {guest_number != null && (
              <div className="flex items-center gap-1">
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                <span>Up to {guest_number}</span>
              </div>
            )}
          </div>
        )}

        {/* Amenity pills */}
        {topAmenities.length > 0 && (
          <div className="flex items-center gap-1.5 flex-wrap mb-3">
            {topAmenities.map((a) => (
              <span key={a.id} className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-gray-50 dark:bg-neutral-700 text-gray-500 dark:text-gray-400 border border-gray-100 dark:border-neutral-600">
                {a.name}
              </span>
            ))}
            {Array.isArray(amenities) && amenities.length > 3 && (
              <span className="text-[10px] text-gray-400">+{amenities.length - 3} more</span>
            )}
          </div>
        )}

        {/* Price + Rating + CTA */}
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-baseline gap-0.5">
              <span className="text-[10px] text-gray-400">From</span>
              <span className="text-lg font-extrabold text-gray-900 dark:text-white ml-1">
                {currencySymbol()}{price_per_month ? Number(price_per_month).toLocaleString() : "—"}
              </span>
              <span className="text-[10px] text-gray-400">/mo</span>
            </div>
            {rating != null && rating > 0 && (
              <div className="flex items-center gap-1 mt-0.5">
                <svg className="w-3 h-3 text-amber-400" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                </svg>
                <span className="text-[10px] font-semibold text-gray-700 dark:text-gray-300">
                  {Number(rating).toFixed(1)}
                </span>
                {reviewCount > 0 && (
                  <span className="text-[10px] text-gray-400">({reviewCount})</span>
                )}
              </div>
            )}
          </div>

          <Link to={href}>
            <button className="px-4 py-2 text-xs font-bold text-white bg-[#f5694b] hover:bg-[#e55a3c] active:bg-[#d44e32] rounded-xl transition-colors shadow-sm hover:shadow-md">
              View →
            </button>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default StayCard;